/**
 * Application constants and configuration values
 * Centralized configuration for API endpoints, routes, AWS settings, and UI constants
 */

// ============================================================================
// API Configuration
// ============================================================================

/**
 * Base URL for API requests
 * Falls back to empty string if not configured in environment
 */
export const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// ============================================================================
// Route Constants
// ============================================================================

/**
 * Application route definitions
 * Centralized routing constants to prevent typos and enable refactoring
 */
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  GALLERY: '/gallery',
  PROFILE: '/profile',
  ADMIN: '/admin',
  ADMIN_USERS: '/admin/users',
  ADMIN_PHOTOS: '/admin/photos',
} as const;

/**
 * Type-safe route names for type checking
 */
export type Route = (typeof ROUTES)[keyof typeof ROUTES];

// ============================================================================
// AWS Configuration
// ============================================================================

/**
 * AWS service configuration
 * Includes Cognito, S3, and other AWS service endpoints
 */
export const AWS_CONFIG = {
  region: import.meta.env.VITE_AWS_REGION || 'af-south-1',
  userPoolId: import.meta.env.VITE_USER_POOL_ID || '',
  userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID || '',
  photosBucket: import.meta.env.VITE_S3_BUCKET || '',
} as const;

/**
 * Cognito OAuth scopes for authentication
 */
export const COGNITO_SCOPES = ['openid', 'email', 'profile'] as const;

/**
 * S3 bucket prefixes for organized storage
 */
export const S3_PREFIXES = {
  ORIGINAL: 'photos/original/',
  THUMBNAIL: 'photos/thumbnails/',
  ALBUM: 'albums/',
} as const;

// ============================================================================
// Authentication Configuration
// ============================================================================

/**
 * Password security requirements
 * Used for both client-side validation and as reference for server-side validation
 */
export const PASSWORD_REQUIREMENTS = {
  minLength: 12,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSymbols: true,
} as const;

/**
 * Session configuration
 */
export const SESSION_CONFIG = {
  /** Session timeout in milliseconds (15 minutes) */
  timeout: 15 * 60 * 1000,
  /** Token refresh threshold in milliseconds (5 minutes before expiry) */
  refreshThreshold: 5 * 60 * 1000,
} as const;

// ============================================================================
// Photo Configuration
// ============================================================================

/**
 * Photo upload and processing configuration
 */
export const PHOTO_CONFIG = {
  /** Maximum file size in bytes (10 MB) */
  maxFileSize: 10 * 1024 * 1024,
  /** Allowed MIME types for uploads */
  allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const,
  /** Thumbnail dimensions for gallery preview */
  thumbnailSize: {
    width: 300,
    height: 300,
  },
  /** Number of photos per grid page */
  gridPageSize: 24,
  /** Allowed image extensions (lowercase) */
  allowedExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'] as const,
  /** EXIF data retention policy - whether to strip EXIF from uploads */
  stripExif: true,
} as const;

/**
 * Photo quality settings for optimization
 */
export const PHOTO_QUALITY = {
  /** JPEG quality for thumbnails (0-100) */
  thumbnailJpegQuality: 80,
  /** JPEG quality for web display (0-100) */
  webJpegQuality: 85,
  /** WebP quality for web display (0-100) */
  webpQuality: 80,
} as const;

// ============================================================================
// UI Configuration
// ============================================================================

/**
 * Toast notification duration in milliseconds
 */
export const TOAST_DURATION = 5000;

/**
 * Debounce delay for input and search operations in milliseconds
 */
export const DEBOUNCE_DELAY = 300;

/**
 * Animation and transition durations in milliseconds
 */
export const ANIMATION_DURATIONS = {
  /** Quick animations (modals, dropdowns) */
  quick: 150,
  /** Standard animations (page transitions) */
  standard: 300,
  /** Slow animations (complex interactions) */
  slow: 500,
} as const;

/**
 * Common responsive breakpoints (in pixels)
 */
export const BREAKPOINTS = {
  xs: 320,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

/**
 * Z-index scale for layering elements
 */
export const Z_INDEX = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  fixed: 300,
  offCanvas: 400,
  modal: 500,
  popover: 600,
  tooltip: 700,
} as const;

// ============================================================================
// Storage Configuration
// ============================================================================

/**
 * Local storage keys
 * Namespaced with 'walrus-' prefix to avoid conflicts
 */
export const STORAGE_KEYS = {
  THEME: 'walrus-theme',
  AUTH_TOKEN: 'walrus-auth-token',
  LAST_ALBUM: 'walrus-last-album',
  USER_PREFERENCES: 'walrus-user-preferences',
  GALLERY_FILTERS: 'walrus-gallery-filters',
  SEARCH_HISTORY: 'walrus-search-history',
} as const;

/**
 * Session storage keys
 * For data that should be cleared when the tab closes
 */
export const SESSION_STORAGE_KEYS = {
  UPLOAD_PROGRESS: 'walrus-upload-progress',
  FORM_DRAFT: 'walrus-form-draft',
} as const;

// ============================================================================
// Error Messages
// ============================================================================

/**
 * User-friendly error messages
 */
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  AUTHENTICATION_ERROR: 'Authentication failed. Please log in again.',
  UNAUTHORIZED_ERROR: 'You do not have permission to perform this action.',
  NOT_FOUND_ERROR: 'The requested resource was not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  FILE_TOO_LARGE: 'File size exceeds the 10 MB limit.',
  INVALID_FILE_TYPE: 'File type not supported. Please use JPEG, PNG, GIF, or WebP.',
  UPLOAD_ERROR: 'Failed to upload photo. Please try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again later.',
} as const;

// ============================================================================
// Success Messages
// ============================================================================

/**
 * User-friendly success messages
 */
export const SUCCESS_MESSAGES = {
  UPLOADED: 'Photo uploaded successfully.',
  DELETED: 'Photo deleted successfully.',
  MOVED: 'Photo moved successfully.',
  PROFILE_UPDATED: 'Profile updated successfully.',
  SETTINGS_SAVED: 'Settings saved successfully.',
} as const;

// ============================================================================
// API Endpoints
// ============================================================================

/**
 * API endpoint paths
 * Relative paths that are appended to API_BASE_URL
 */
export const API_ENDPOINTS = {
  // Authentication
  AUTH_LOGIN: '/auth/login',
  AUTH_LOGOUT: '/auth/logout',
  AUTH_REFRESH: '/auth/refresh',

  // Photos
  PHOTOS_LIST: '/photos',
  PHOTOS_PRESIGNED_URL: '/photos/presigned-url',
  PHOTOS_UPDATE: '/photos/:id',
  PHOTOS_DELETE: '/photos/:id',
  PHOTOS_DETAILS: '/photos/:id',

  // Albums
  ALBUMS_LIST: '/albums',
  ALBUMS_CREATE: '/albums',
  ALBUMS_DELETE: '/albums/:id',

  // Users
  USERS_PROFILE: '/users/profile',
  USERS_UPDATE: '/users/profile',
  USERS_DELETE: '/users/account',

  // Admin
  ADMIN_USERS_LIST: '/admin/users',
  ADMIN_PHOTOS_LIST: '/admin/photos',
} as const;

// ============================================================================
// Validation Rules
// ============================================================================

/**
 * Validation rules for common fields
 */
export const VALIDATION_RULES = {
  email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email address',
  },
  username: {
    minLength: 3,
    maxLength: 30,
    pattern: /^[a-zA-Z0-9_-]+$/,
    message: 'Username must be 3-30 characters and contain only letters, numbers, underscores, and hyphens',
  },
  firstName: {
    minLength: 1,
    maxLength: 50,
    message: 'First name must be 1-50 characters',
  },
  lastName: {
    minLength: 1,
    maxLength: 50,
    message: 'Last name must be 1-50 characters',
  },
} as const;

// ============================================================================
// Rate Limiting
// ============================================================================

/**
 * Rate limiting configuration for API requests
 */
export const RATE_LIMITS = {
  /** Photos upload - requests per minute */
  photoUpload: 10,
  /** Photos fetch - requests per minute */
  photoFetch: 30,
  /** Search - requests per minute */
  search: 20,
  /** General API - requests per minute */
  general: 60,
} as const;

// ============================================================================
// Pagination
// ============================================================================

/**
 * Pagination configuration
 */
export const PAGINATION = {
  /** Default page size for list endpoints */
  defaultPageSize: 20,
  /** Maximum page size allowed */
  maxPageSize: 100,
} as const;
